import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";

const API_BASE = import.meta.env.VITE_API_URL || "/api";

interface AdminOrg {
  id: string;
  name: string;
  slug: string;
  plan: string;
  domainLimit: number;
  messageLimit: number;
  monthlyMessageCount: number;
  memberCount: number;
  domainCount: number;
  createdAt: string;
}

interface OrgsResponse {
  organizations: AdminOrg[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

async function fetchOrganizations(page: number, search: string, plan: string): Promise<OrgsResponse> {
  const params = new URLSearchParams({ page: String(page), limit: "25" });
  if (search) params.set("search", search);
  if (plan) params.set("plan", plan);

  const res = await fetch(`${API_BASE}/admin/organizations?${params}`, {
    credentials: "include",
  });
  if (!res.ok) throw new Error("Failed to fetch organizations");
  return res.json();
}

export function AdminOrganizationsPage() {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [planFilter, setPlanFilter] = useState("");

  const { data, isLoading } = useQuery({
    queryKey: ["admin-organizations", page, search, planFilter],
    queryFn: () => fetchOrganizations(page, search, planFilter),
  });

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setSearch(searchInput);
    setPage(1);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="page-header page-header-primary">
        <div className="relative z-10">
          <div className="flex items-center gap-2 text-indigo-200 mb-2">
            <Link to="/admin" className="hover:text-white">Admin</Link>
            <span>/</span>
            <span>Organizations</span>
          </div>
          <h1 className="text-3xl font-bold">All Organizations</h1>
          <p className="mt-1 text-indigo-100">
            {data?.pagination.total || 0} total organizations
          </p>
        </div>
        <div className="page-header-decor" />
        <div className="page-header-decor-alt" />
      </div>

      {/* Filters */}
      <div className="card">
        <div className="flex flex-col sm:flex-row gap-4">
          <form onSubmit={handleSearch} className="flex-1 flex gap-2">
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search by name..."
              className="input input-primary flex-1"
            />
            <button type="submit" className="btn btn-primary">
              Search
            </button>
          </form>
          <select
            value={planFilter}
            onChange={(e) => {
              setPlanFilter(e.target.value);
              setPage(1);
            }}
            className="input input-primary sm:w-40"
          >
            <option value="">All Plans</option>
            <option value="free">Free</option>
            <option value="starter">Starter</option>
            <option value="pro">Pro</option>
            <option value="business">Business</option>
            <option value="enterprise">Enterprise</option>
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="card overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center text-muted-foreground">Loading...</div>
        ) : data?.organizations && data.organizations.length > 0 ? (
          <>
            <div className="overflow-x-auto">
              <table className="table">
                <thead>
                  <tr className="table-header">
                    <th className="table-header-cell">Organization</th>
                    <th className="table-header-cell">Plan</th>
                    <th className="table-header-cell text-right">Members</th>
                    <th className="table-header-cell text-right">Domains</th>
                    <th className="table-header-cell text-right">Usage</th>
                    <th className="table-header-cell text-right">Created</th>
                  </tr>
                </thead>
                <tbody>
                  {data.organizations.map((org) => (
                    <tr key={org.id} className="table-row">
                      <td className="table-cell">
                        <Link
                          to={`/admin/organizations/${org.id}` as any}
                          className="font-medium text-indigo-600 hover:text-indigo-500"
                        >
                          {org.name}
                        </Link>
                        <div className="text-sm text-muted-foreground">{org.slug}</div>
                      </td>
                      <td className="table-cell">
                        <span className="badge badge-primary">
                          {org.plan.charAt(0).toUpperCase() + org.plan.slice(1)}
                        </span>
                      </td>
                      <td className="table-cell text-right">{org.memberCount}</td>
                      <td className="table-cell text-right">{org.domainCount}</td>
                      <td className="table-cell text-right">
                        <div>{org.monthlyMessageCount.toLocaleString()}</div>
                        <div className="text-xs text-muted-foreground">
                          / {org.messageLimit.toLocaleString()}
                        </div>
                      </td>
                      <td className="table-cell text-right text-sm text-muted-foreground">
                        {new Date(org.createdAt).toLocaleDateString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {data.pagination.totalPages > 1 && (
              <div className="flex items-center justify-between border-t px-4 py-3">
                <div className="text-sm text-muted-foreground">
                  Page {data.pagination.page} of {data.pagination.totalPages}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="btn btn-secondary btn-sm"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setPage((p) => Math.min(data.pagination.totalPages, p + 1))}
                    disabled={page === data.pagination.totalPages}
                    className="btn btn-secondary btn-sm"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="p-8 text-center text-muted-foreground">
            No organizations found
          </div>
        )}
      </div>
    </div>
  );
}
